import { frameId, propFeet, type CompileContext, type LayerDocument, type MapDocument, type ValidationIssue, type WorldDocument } from "daedalus-core";
import { SIXTEENTHS } from "../movement/step.ts";
import { shrineGround } from "../shrines/ground.ts";
import { SHRINE_START_FIELD, isStartingShrine, startingShrine } from "../shrines/start.ts";
import { SHRINE, SPAWN_POINT } from "./type-names.ts";

/**
 * The starting shrine in a document (ADR-096's amendment of 2026-10-05). A shrine, which is a Shrine prop, carries
 * the boolean field `start`, "Starting shrine": every character knows that shrine without walking to it. A map
 * holds one at most, and may hold none: its characters then start at the Spawn Point and know no shrine.
 *
 * The choice is the builder's, and a read never undoes it. A shrine that carries the field, ticked or not, says its
 * builder has chosen: placing a shrine writes `start: false` and the checkbox writes `true` or `false`. Only a map
 * none of whose shrines carries the field, one saved before the field existed, is upgraded as it is read: the shrine
 * nearest its Spawn Point is marked (`startingShrine`: straight line from the Spawn Point to the
 * prop's ground point, the lower `y`, then the lower `x`, then the earlier in the document where two are as near) and
 * every other shrine of the map is written `start: false`, so the map is settled and no later read marks another. A
 * map with no shrine or no Spawn Point is left alone.
 */

/**
 * One shrine the upgrade marked: the map, the layer, the prop it is, and its name.
 */
export interface ShrineStartReport {
  readonly mapId: string;
  readonly layerId: string;
  readonly propId: string;
  readonly name: string;
}

/**
 * A shrine of a map as the document holds it: its layer, its prop, where it stands in tiles and its fields.
 */
interface PlacedShrine {
  readonly layerId: string;
  readonly propId: string;
  readonly x: number;
  readonly y: number;
  readonly fields: Readonly<Record<string, unknown>>;
}

/**
 * How a shrine is named in a sentence.
 *
 * @param shrine The shrine.
 * @returns `prop <id>`.
 */
function called(shrine: Pick<PlacedShrine, "propId">): string {
  return `prop ${shrine.propId}`;
}

/**
 * Every shrine of a map, layer by layer in document order: its Shrine props at their
 * ground points, the stone's base, where the context knows the frame's footprint, and at their feet points otherwise.
 *
 * @param map The map.
 * @param context The world's compile context, for the footprints, or null to place every prop at its anchor.
 * @param tileSize The world's tile size in pixels.
 * @returns The shrines.
 */
function shrinesOf(map: MapDocument, context: Pick<CompileContext, "collision"> | null, tileSize: number): PlacedShrine[] {
  const shrines: PlacedShrine[] = [];
  for (const layer of map.layers) {
    if (layer.kind === "prop") {
      for (const prop of layer.props) {
        if (prop.behaviour?.type !== SHRINE) {
          continue;
        }
        const footprint = context?.collision.footprints?.[frameId(prop.frame.tileset, null, prop.frame.tile)];
        if (footprint === undefined) {
          shrines.push({ layerId: layer.id, propId: prop.id, x: prop.x, y: prop.y, fields: prop.behaviour.fields });
          continue;
        }
        const feet = propFeet(prop, footprint.w, tileSize);
        const ground = shrineGround({ x: feet.x * SIXTEENTHS, y: feet.y * SIXTEENTHS, footprint, flipX: prop.flipX, flipY: prop.flipY }, tileSize);
        shrines.push({ layerId: layer.id, propId: prop.id, x: ground.x / SIXTEENTHS, y: ground.y / SIXTEENTHS, fields: prop.behaviour.fields });
      }
    }
  }
  return shrines;
}

/**
 * Whether a shrine says if it is the starting one: its fields carry `start`, whatever its value.
 *
 * @param shrine The shrine.
 * @returns True when the field is there.
 */
function chosen(shrine: PlacedShrine): boolean {
  return Object.hasOwn(shrine.fields, SHRINE_START_FIELD);
}

/**
 * One map with its starting shrine marked and its other shrines written as not the starting one, when it has shrines,
 * a Spawn Point and no shrine that says either way.
 *
 * @param map The map.
 * @param context The world's compile context.
 * @param tileSize The world's tile size in pixels.
 * @param reports Where the marked shrine is recorded.
 * @returns The map, the same object when nothing was marked.
 */
function markedMap(map: MapDocument, context: Pick<CompileContext, "collision">, tileSize: number, reports: ShrineStartReport[]): MapDocument {
  const shrines = shrinesOf(map, context, tileSize);
  if (shrines.length === 0 || shrines.some(chosen)) {
    return map;
  }
  const spawn = map.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities : [])).find((entity) => entity.type === SPAWN_POINT);
  const nearest = shrines[startingShrine(shrines.map((shrine) => ({ x: shrine.x, y: shrine.y, start: false })), spawn === undefined ? null : { x: spawn.x, y: spawn.y })];
  if (nearest === undefined) {
    return map;
  }
  const name = nearest.fields["name"];
  reports.push({ mapId: map.id, layerId: nearest.layerId, propId: nearest.propId, name: typeof name === "string" ? name : "" });
  const layers = map.layers.map((layer): LayerDocument => {
    if (layer.kind === "prop") {
      return layer.props.some((prop) => prop.behaviour?.type === SHRINE) ? { ...layer, props: layer.props.map((prop) => (prop.behaviour?.type === SHRINE ? { ...prop, behaviour: { ...prop.behaviour, fields: { ...prop.behaviour.fields, [SHRINE_START_FIELD]: layer.id === nearest.layerId && prop.id === nearest.propId } } } : prop)) } : layer;
    }
    return layer;
  });
  return { ...map, layers };
}

/**
 * Marks the starting shrine of every map that has shrines, a Spawn Point and no shrine carrying `start`, and writes
 * the map's other shrines as not the starting one. A map on which any shrine carries the field is its builder's.
 *
 * @param world The document.
 * @param context The world's compile context, for the footprints a prop's ground point is read from.
 * @returns The document, the same object when nothing was marked, and one report per marked shrine in map order.
 */
export function markStartingShrines(world: WorldDocument, context: Pick<CompileContext, "collision">): { readonly world: WorldDocument; readonly starts: readonly ShrineStartReport[] } {
  const starts: ShrineStartReport[] = [];
  const maps = world.maps.map((map) => markedMap(map, context, world.tileSize, starts));
  return starts.length === 0 ? { world, starts } : { world: { ...world, maps }, starts };
}

/**
 * Refuses a map that marks more than one starting shrine: each marked shrine is named with the first other. The
 * kind is `shrine.start-twice`, which nothing ignores, since the map does not compile.
 *
 * @param map The map.
 * @returns One error per marked shrine when two or more are marked, none otherwise.
 */
export function shrineStartIssues(map: MapDocument): ValidationIssue[] {
  const marked = shrinesOf(map, null, 1).filter((shrine) => isStartingShrine(shrine.fields));
  if (marked.length < 2) {
    return [];
  }
  return marked.map((shrine, index): ValidationIssue => {
    const other = marked[index === 0 ? 1 : 0] as PlacedShrine;
    return {
      mapId: map.id,
      kind: "shrine.start-twice",
      layerId: shrine.layerId,
      propId: shrine.propId,
      message: `${called(shrine)} and ${called(other)} are both marked Starting shrine, and a map has one such shrine; untick Starting shrine on one of them`,
    };
  });
}

/**
 * One line for a shrine the upgrade marked, for a compile's warnings and `world:migrate`.
 *
 * @param report The report.
 * @returns The line.
 */
export function describeShrineStart(report: ShrineStartReport): string {
  return `shrine ${report.propId} ${JSON.stringify(report.name)} (map ${report.mapId}, layer ${report.layerId}): marked Starting shrine, being the shrine nearest the Spawn Point on a map saved before a shrine could be so marked; untick it in the editor and save to start characters at the Spawn Point alone`;
}

/**
 * The editor's notes on the shrines marked as a map's document opened: a warning on each, saying why it was marked,
 * that a save keeps it and that unticking it leaves the map with none. The kind is `shrine.start-marked`, which is
 * not ignorable: a save clears it, and an ignore would outlive what it hid. A shrine no longer on the map is not noted.
 *
 * @param starts The reports of the upgrade.
 * @param map The map in view.
 * @returns The warnings, in report order.
 */
export function shrineStartNotes(starts: readonly ShrineStartReport[], map: MapDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const start of starts) {
    if (start.mapId !== map.id) {
      continue;
    }
    const placed = shrinesOf(map, null, 1).find((shrine) => shrine.layerId === start.layerId && shrine.propId === start.propId);
    if (placed === undefined) {
      continue;
    }
    issues.push({
      mapId: map.id,
      kind: "shrine.start-marked",
      layerId: start.layerId,
      propId: start.propId,
      severity: "warning",
      message: `${called(start)} ${JSON.stringify(start.name)} is the shrine nearest the Spawn Point, and this map was saved before a shrine could be marked Starting shrine, so it was marked as the world was opened: every character knows it without walking to it; save to keep it, or untick Starting shrine and save to start characters at the Spawn Point alone`,
    });
  }
  return issues;
}
